import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { behaviors } from "@/content/behaviors";
import {
  buildReport,
  type Answers,
  type Relationship,
  type ReportPayload,
  type RawCycleReport,
} from "@/content/review";
import { SITE_URL } from "@/lib/seo";

const RELATIONSHIPS: Relationship[] = ["self", "direct_report", "peer", "manager"];
const BEHAVIOR_SLUGS = new Set(behaviors.map((b) => b.slug as string));

/** Keeps only known behaviour slugs and valid 1-5 scores (null = not enough visibility). */
function cleanAnswers(answers: Answers): { behavior_slug: string; score: number | null }[] {
  return Object.entries(answers)
    .filter(([slug]) => BEHAVIOR_SLUGS.has(slug))
    .map(([slug, score]) => ({
      behavior_slug: slug,
      score: typeof score === "number" && score >= 1 && score <= 5 ? Math.round(score) : null,
    }));
}

function assertRelationship(value: string): Relationship {
  if (!RELATIONSHIPS.includes(value as Relationship)) throw new Error("Unknown rater type.");
  return value as Relationship;
}

/* ------------------------------------------------------------------ leader */

export interface CycleSummary {
  id: string;
  leaderName: string | null;
  status: string;
  unlocked: boolean;
  createdAt: string;
  selfComplete: boolean;
  submitted: Record<Relationship, number>;
  invited: Record<Relationship, number>;
}

export const listMyCycles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CycleSummary[]> => {
    const { data: cycles, error } = await context.supabase
      .from("review_cycles")
      .select("id, leader_name, status, unlocked, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error("We couldn't load your reviews right now.");

    const ids = (cycles ?? []).map((c) => c.id);
    const { data: raters } = ids.length
      ? await context.supabase
          .from("cycle_raters")
          .select("cycle_id, relationship, submitted_at")
          .in("cycle_id", ids)
      : { data: [] };

    return (cycles ?? []).map((c) => {
      const mine = (raters ?? []).filter((r) => r.cycle_id === c.id);
      const empty = { self: 0, direct_report: 0, peer: 0, manager: 0 };
      const submitted = { ...empty } as Record<Relationship, number>;
      const invited = { ...empty } as Record<Relationship, number>;
      for (const r of mine) {
        const rel = r.relationship as Relationship;
        invited[rel] += 1;
        if (r.submitted_at) submitted[rel] += 1;
      }
      return {
        id: c.id,
        leaderName: c.leader_name,
        status: c.status,
        unlocked: c.unlocked,
        createdAt: c.created_at,
        selfComplete: submitted.self > 0,
        submitted,
        invited,
      };
    });
  });

export const createCycle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { leaderName?: string }) => data)
  .handler(async ({ data, context }): Promise<{ cycleId: string }> => {
    const { data: row, error } = await context.supabase
      .from("review_cycles")
      .insert({
        leader_user_id: context.userId,
        leader_name: data.leaderName?.trim() || null,
        status: "open",
        opened_at: new Date().toISOString(),
      })
      .select("id")
      .single();
    if (error || !row) throw new Error("We couldn't start a review right now.");
    return { cycleId: row.id };
  });

/** Confirms the signed-in user owns this cycle before any privileged write. */
async function assertOwnCycle(
  supabase: { from: (t: string) => any }, // eslint-disable-line @typescript-eslint/no-explicit-any
  cycleId: string,
): Promise<void> {
  const { data, error } = await supabase
    .from("review_cycles")
    .select("id")
    .eq("id", cycleId)
    .maybeSingle();
  if (error || !data) throw new Error("Review not found.");
}

export const saveSelfRating = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string; answers: Answers }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertOwnCycle(context.supabase, data.cycleId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("cycle_raters")
      .select("id, submitted_at")
      .eq("cycle_id", data.cycleId)
      .eq("relationship", "self")
      .maybeSingle();

    let raterId = existing?.id;
    if (!raterId) {
      const { createInviteToken, hashInviteToken } = await import("@/lib/review.server");
      const { data: created, error } = await supabaseAdmin
        .from("cycle_raters")
        .insert({
          cycle_id: data.cycleId,
          relationship: "self",
          invite_token_hash: await hashInviteToken(createInviteToken()),
          submitted_at: new Date().toISOString(),
        })
        .select("id")
        .single();
      if (error || !created) throw new Error("We couldn't save your answers.");
      raterId = created.id;
    } else if (!existing?.submitted_at) {
      await supabaseAdmin
        .from("cycle_raters")
        .update({ submitted_at: new Date().toISOString() })
        .eq("id", raterId);
    }

    const rows = cleanAnswers(data.answers).map((r) => ({
      cycle_id: data.cycleId,
      rater_id: raterId!,
      ...r,
    }));
    if (rows.length) {
      const { error } = await supabaseAdmin
        .from("cycle_ratings")
        .upsert(rows, { onConflict: "rater_id,behavior_slug" });
      if (error) throw new Error("We couldn't save your answers.");
    }
    return { ok: true };
  });

export const addRater = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string; relationship: string; email?: string }) => data)
  .handler(
    async ({
      data,
      context,
    }): Promise<{ raterId: string; token: string; link: string; emailSent: boolean }> => {
      const relationship = assertRelationship(data.relationship);
      if (relationship === "self")
        throw new Error("Your own rating is saved from the review page.");
      await assertOwnCycle(context.supabase, data.cycleId);

      const { createInviteToken, hashInviteToken, MAX_RATERS_PER_CYCLE } =
        await import("@/lib/review.server");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const { count } = await supabaseAdmin
        .from("cycle_raters")
        .select("id", { count: "exact", head: true })
        .eq("cycle_id", data.cycleId);
      if ((count ?? 0) >= MAX_RATERS_PER_CYCLE) {
        throw new Error("This review already has the maximum number of raters.");
      }

      const token = createInviteToken();
      const email = data.email?.trim() || null;
      const { data: row, error } = await supabaseAdmin
        .from("cycle_raters")
        .insert({
          cycle_id: data.cycleId,
          relationship,
          invite_token_hash: await hashInviteToken(token),
          invited_email: email,
        })
        .select("id")
        .single();
      if (error || !row) {
        // The one-self / one-manager indexes surface here.
        throw new Error(
          relationship === "manager"
            ? "You can only add one manager to a review."
            : "We couldn't add that person.",
        );
      }

      const link = `${SITE_URL}/review/${token}/`;
      let emailSent = false;
      if (email) {
        try {
          const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
          const { data: cycle } = await context.supabase
            .from("review_cycles")
            .select("leader_name")
            .eq("id", data.cycleId)
            .maybeSingle();
          await sendTemplateEmail("review-rater-invite", email, {
            templateData: {
              leaderName: cycle?.leader_name || "your colleague",
              relationshipLine:
                relationship === "direct_report"
                  ? "one of their direct reports"
                  : relationship === "peer"
                    ? "one of their peers"
                    : "their manager",
              reviewUrl: link,
              identified: relationship === "manager",
            },
          });
          await supabaseAdmin
            .from("cycle_raters")
            .update({ invite_sent_at: new Date().toISOString() })
            .eq("id", row.id);
          emailSent = true;
        } catch (sendError) {
          // The link still works; only the email failed.
          console.error("[review] rater invite email failed", sendError);
        }
      }

      return { raterId: row.id, token, link, emailSent };
    },
  );

export const rotateRaterToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string; raterId: string }) => data)
  .handler(async ({ data, context }): Promise<{ token: string; link: string }> => {
    await assertOwnCycle(context.supabase, data.cycleId);
    const { createInviteToken, hashInviteToken } = await import("@/lib/review.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const token = createInviteToken();
    const { error } = await supabaseAdmin
      .from("cycle_raters")
      .update({ invite_token_hash: await hashInviteToken(token) })
      .eq("id", data.raterId)
      .eq("cycle_id", data.cycleId)
      .is("submitted_at", null);
    if (error) throw new Error("We couldn't reissue that link.");
    return { token, link: `${SITE_URL}/review/${token}/` };
  });

export const removeRater = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string; raterId: string }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { error } = await context.supabase
      .from("cycle_raters")
      .delete()
      .eq("id", data.raterId)
      .eq("cycle_id", data.cycleId);
    if (error) throw new Error("We couldn't remove that person.");
    return { ok: true };
  });

export interface RaterRow {
  id: string;
  relationship: Relationship;
  invitedEmail: string | null;
  submitted: boolean;
}

export const cycleDetail = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string }) => data)
  .handler(
    async ({
      data,
      context,
    }): Promise<{
      id: string;
      leaderName: string | null;
      status: string;
      unlocked: boolean;
      selfComplete: boolean;
      raters: RaterRow[];
    }> => {
      const { data: cycle, error } = await context.supabase
        .from("review_cycles")
        .select("id, leader_name, status, unlocked")
        .eq("id", data.cycleId)
        .maybeSingle();
      if (error || !cycle) throw new Error("Review not found.");

      const { data: raters } = await context.supabase
        .from("cycle_raters")
        .select("id, relationship, invited_email, submitted_at")
        .eq("cycle_id", data.cycleId)
        .order("created_at", { ascending: true });

      const rows = (raters ?? []).map((r) => ({
        id: r.id,
        relationship: r.relationship as Relationship,
        invitedEmail: r.invited_email,
        submitted: Boolean(r.submitted_at),
      }));

      return {
        id: cycle.id,
        leaderName: cycle.leader_name,
        status: cycle.status,
        unlocked: cycle.unlocked,
        selfComplete: rows.some((r) => r.relationship === "self" && r.submitted),
        raters: rows.filter((r) => r.relationship !== "self"),
      };
    },
  );

export const closeCycle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { error } = await context.supabase
      .from("review_cycles")
      .update({ status: "closed" })
      .eq("id", data.cycleId);
    if (error) throw new Error("We couldn't close that review.");
    return { ok: true };
  });

/**
 * The only way scores reach the leader. cycle_report() applies the anonymity
 * thresholds inside the database; buildReport() then drops the locked detail
 * so it is never in the response at all.
 */
export const getCycleReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string }) => data)
  .handler(async ({ data, context }): Promise<ReportPayload> => {
    const { data: raw, error } = await context.supabase.rpc("cycle_report", {
      _cycle_id: data.cycleId,
    });
    if (error || !raw) throw new Error("We couldn't load this report right now.");
    return buildReport(raw as unknown as RawCycleReport);
  });

/* -------------------------------------------------------------------- staff */

export interface AdminCycleRow {
  id: string;
  leaderName: string | null;
  status: string;
  unlocked: boolean;
  createdAt: string;
  submitted: Record<Relationship, number>;
  lastSubmittedAt: string | null;
}

/** Role check runs as the caller, through the has_role helper. */
async function assertAdmin(context: {
  // The generated Supabase client type is too specific to name here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: any;
  userId: string;
}): Promise<void> {
  const { data } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (data !== true) throw new Error("Forbidden");
}

export const adminListCycles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminCycleRow[]> => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: cycles } = await supabaseAdmin
      .from("review_cycles")
      .select("id, leader_name, status, unlocked, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    const ids = (cycles ?? []).map((c) => c.id);
    const { data: raters } = ids.length
      ? await supabaseAdmin
          .from("cycle_raters")
          .select("cycle_id, relationship, submitted_at")
          .in("cycle_id", ids)
      : { data: [] };

    return (cycles ?? []).map((c) => {
      const mine = (raters ?? []).filter((r) => r.cycle_id === c.id && r.submitted_at);
      const submitted = { self: 0, direct_report: 0, peer: 0, manager: 0 } as Record<
        Relationship,
        number
      >;
      for (const r of mine) submitted[r.relationship as Relationship] += 1;
      const stamps = mine.map((r) => r.submitted_at as string).sort();
      return {
        id: c.id,
        leaderName: c.leader_name,
        status: c.status,
        unlocked: c.unlocked,
        createdAt: c.created_at,
        submitted,
        lastSubmittedAt: stamps.length ? stamps[stamps.length - 1]! : null,
      };
    });
  });

export const adminSetUnlocked = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string; unlocked: boolean }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("review_cycles")
      .update({ unlocked: data.unlocked })
      .eq("id", data.cycleId);
    if (error) throw new Error("We couldn't change that review.");
    return { ok: true };
  });

/**
 * Staff read of a leader's full report, whether or not it has been opened for
 * the leader. Lets a Development Pro prepare before the conversation without
 * unlocking the leader's own view first.
 */
export const adminGetCycleReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string }) => data)
  .handler(
    async ({
      data,
      context,
    }): Promise<{ leaderName: string | null; unlocked: boolean; report: ReportPayload }> => {
      await assertAdmin(context);
      const { data: raw, error } = await context.supabase.rpc("cycle_report", {
        _cycle_id: data.cycleId,
      });
      if (error || !raw) throw new Error("We couldn't load that report right now.");

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: cycle } = await supabaseAdmin
        .from("review_cycles")
        .select("leader_name, unlocked")
        .eq("id", data.cycleId)
        .maybeSingle();

      // Staff always see the detail; the leader's own view still follows `unlocked`.
      const report = buildReport({ ...(raw as unknown as RawCycleReport), unlocked: true });
      return {
        leaderName: cycle?.leader_name ?? null,
        unlocked: Boolean(cycle?.unlocked),
        report,
      };
    },
  );

/**
 * Tells the support inbox that this leader wants the conversation, so nobody
 * has to hunt for the check. Email trouble never blocks the leader.
 */
export const requestAdvisorConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { cycleId: string }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { data: cycle } = await context.supabase
      .from("review_cycles")
      .select("id, leader_name")
      .eq("id", data.cycleId)
      .maybeSingle();
    if (!cycle) throw new Error("Review not found.");

    try {
      const { data: profile } = await context.supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", context.userId)
        .maybeSingle();

      const { data: raters } = await context.supabase
        .from("cycle_raters")
        .select("relationship, submitted_at")
        .eq("cycle_id", data.cycleId);

      const counts = { direct_report: 0, peer: 0, manager: 0 } as Record<string, number>;
      for (const r of raters ?? []) {
        if (r.submitted_at && r.relationship in counts) counts[r.relationship] += 1;
      }

      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      await sendTemplateEmail("review-conversation-request", "support@leadershipedge.live", {
        templateData: {
          leaderName: cycle.leader_name || profile?.full_name || null,
          leaderEmail: profile?.email || null,
          cycleId: cycle.id,
          adminUrl: `${SITE_URL}/admin/review/`,
          responseSummary: `Direct reports ${counts["direct_report"]}, peers ${counts["peer"]}, manager ${counts["manager"]}`,
        },
        replyTo: profile?.email || undefined,
      });
    } catch (sendError) {
      console.error("[review] conversation request email failed", sendError);
    }

    return { ok: true };
  });

export const amIReviewAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ admin: boolean }> => {
    const { data } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    return { admin: data === true };
  });

/* -------------------------------------------------------------------- rater */

export interface RaterContext {
  found: boolean;
  submitted: boolean;
  closed: boolean;
  relationship: Relationship | null;
  leaderName: string | null;
  answers: Answers;
}

/** Looks a rater up by the hash of their link token. */
async function findRater(token: string) {
  const { hashInviteToken } = await import("@/lib/review.server");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const hash = await hashInviteToken(token);
  const { data } = await supabaseAdmin
    .from("cycle_raters")
    .select("id, cycle_id, relationship, submitted_at")
    .eq("invite_token_hash", hash)
    .maybeSingle();
  return { rater: data, supabaseAdmin };
}

export const getRaterContext = createServerFn({ method: "GET" })
  .inputValidator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<RaterContext> => {
    const empty: RaterContext = {
      found: false,
      submitted: false,
      closed: false,
      relationship: null,
      leaderName: null,
      answers: {},
    };
    if (!data.token || data.token.length < 20) return empty;

    const { rater, supabaseAdmin } = await findRater(data.token);
    if (!rater) return empty;

    const { data: cycle } = await supabaseAdmin
      .from("review_cycles")
      .select("leader_name, status, closes_at")
      .eq("id", rater.cycle_id)
      .maybeSingle();

    const closed =
      cycle?.status === "closed" ||
      Boolean(cycle?.closes_at && new Date(cycle.closes_at) < new Date());

    const { data: saved } = await supabaseAdmin
      .from("cycle_ratings")
      .select("behavior_slug, score")
      .eq("rater_id", rater.id);

    const answers: Answers = {};
    for (const row of saved ?? []) answers[row.behavior_slug] = row.score;

    return {
      found: true,
      submitted: Boolean(rater.submitted_at),
      closed,
      relationship: rater.relationship as Relationship,
      leaderName: cycle?.leader_name ?? null,
      answers,
    };
  });

async function writeRaterAnswers(token: string, answers: Answers, submit: boolean) {
  const { rater, supabaseAdmin } = await findRater(token);
  if (!rater) throw new Error("This link is no longer valid.");
  if (rater.submitted_at) throw new Error("This feedback has already been submitted.");

  const { data: cycle } = await supabaseAdmin
    .from("review_cycles")
    .select("status, closes_at")
    .eq("id", rater.cycle_id)
    .maybeSingle();
  if (cycle?.status === "closed" || (cycle?.closes_at && new Date(cycle.closes_at) < new Date())) {
    throw new Error("This review is closed.");
  }

  const rows = cleanAnswers(answers).map((r) => ({
    cycle_id: rater.cycle_id,
    rater_id: rater.id,
    ...r,
  }));
  if (rows.length) {
    const { error } = await supabaseAdmin
      .from("cycle_ratings")
      .upsert(rows, { onConflict: "rater_id,behavior_slug" });
    if (error) throw new Error("We couldn't save your answers.");
  }

  if (submit) {
    // Guarded on submitted_at so a double click cannot count twice.
    await supabaseAdmin
      .from("cycle_raters")
      .update({ submitted_at: new Date().toISOString() })
      .eq("id", rater.id)
      .is("submitted_at", null);
  }
  return { ok: true as const };
}

export const saveRaterProgress = createServerFn({ method: "POST" })
  .inputValidator((data: { token: string; answers: Answers }) => data)
  .handler(async ({ data }) => writeRaterAnswers(data.token, data.answers, false));

export const submitRater = createServerFn({ method: "POST" })
  .inputValidator((data: { token: string; answers: Answers }) => data)
  .handler(async ({ data }) => writeRaterAnswers(data.token, data.answers, true));
